import React, { useState, useEffect } from 'react';
import { MasterFlightRecord } from '../../../../../../../utils/types';
import { FormInput, FormSection, ServiceTypeSelect } from '../../../../../common';
import { useDuplicateFlightDetection } from '../../../../../../../hooks/useDuplicateFlightDetection';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import { useFlightNumber } from './useFlightNumber';
import { SplitFlightNumberInput } from './SplitFlightNumberInput';
import { useInputTransformers } from '../../hooks';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

type UpdateDraftFn = <K extends keyof MasterFlightRecord>(
  key: K,
  value: MasterFlightRecord[K]
) => void;

interface FlightInformationSectionProps {
  draft: MasterFlightRecord;
  updateDraft: UpdateDraftFn;
  setDraft: React.Dispatch<React.SetStateAction<MasterFlightRecord>>;
  editMode: boolean;
  isVariantMode: boolean;
  isAdhocMode?: boolean;
  getFieldError: (field: keyof MasterFlightRecord) => string | null;
  handleBlur: (field: keyof MasterFlightRecord) => void;
  revalidateIfTouched: (field: keyof MasterFlightRecord, value: MasterFlightRecord[keyof MasterFlightRecord]) => void;
}

/**
 * Flight Information Section
 * Contains: Flight Owner, Flight Number (split), Suffix, Service Type
 * Note: Variant ID is auto-generated based on duplicate detection
 */
export function FlightInformationSection({
  draft,
  updateDraft,
  setDraft,
  editMode,
  isVariantMode,
  isAdhocMode,
  getFieldError,
  handleBlur,
  revalidateIfTouched,
}: FlightInformationSectionProps) {
  // In adhoc mode with a parent selected OR editing an existing adhoc, lock flight info fields
  const adhocLocked = (isAdhocMode && !!draft.parentFlightId) || (editMode && !!draft.isAdhoc);
  const [isDismissed, setIsDismissed] = useState(false);
  const [flightNumberPrefix, setFlightNumberPrefix] = useState('');
  const { flightNumberDigits, handleFlightNumberDigitsChange } = useFlightNumber(draft, updateDraft);
  const {
    transformFlightOwner,
    strictTransformFlightOwner,
    transformSuffix,
  } = useInputTransformers();

  // Initialize flight number prefix from existing flightNo on mount
  useEffect(() => {
    if (draft.flightNo) {
      const match = draft.flightNo.match(/^([A-Z0-9]{1,3})\s/);
      if (match) {
        setFlightNumberPrefix(match[1]);
      }
    }
  }, []); // Only run on mount

  // Duplicate detection - only check in create mode
  // Backend uses airline-specific patterns:
  // - Per-Season (MH, AA, etc.): Variants reset for each season
  // - Global (OD): Variants continue across all seasons
  // Note: Uses flight number prefix (not flightOwner) for duplicate detection
  const duplicateCheck = useDuplicateFlightDetection({
    flightOwner: flightNumberPrefix, // Use prefix for matching
    flightNo: flightNumberDigits,
    seasonId: draft.seasonId, // Required: backend determines per-season vs global logic
    excludeId: editMode ? draft._id : undefined,
    enabled: !editMode && !!flightNumberPrefix && !!flightNumberDigits && !!draft.seasonId,
  });

  // Reset dismissed state when flight info changes
  useEffect(() => {
    setIsDismissed(false);
  }, [draft.flightOwner, flightNumberDigits]);

  // Handle Flight Owner change - SEPARATE from flight number prefix
  const handleFlightOwnerChange = (value: string) => {
    const filtered = transformFlightOwner(value);
    updateDraft('flightOwner', filtered);
    revalidateIfTouched('flightOwner', filtered);
  };

  // Handle Flight Owner blur - apply strict formatting
  const handleFlightOwnerBlur = () => {
    const strict = strictTransformFlightOwner(draft.flightOwner);
    if (strict !== draft.flightOwner) {
      updateDraft('flightOwner', strict);
    }
    handleBlur('flightOwner');
  };

  // Handle Flight Number Prefix change (e.g., "AK", "D7", "MH")
  const handleFlightNumberPrefixChange = (value: string) => {
    const filtered = value.replace(/[^A-Z0-9]/g, '').slice(0, 3);
    setFlightNumberPrefix(filtered);

    // Update full flight number
    const newFlightNo = filtered && flightNumberDigits
      ? `${filtered} ${flightNumberDigits}`
      : filtered || flightNumberDigits;
    updateDraft('flightNo', newFlightNo);
    revalidateIfTouched('flightNo', newFlightNo);
  };

  const handleSuffixChange = (value: string) => {
    const filtered = transformSuffix(value);
    updateDraft('operationalSuffix', filtered);
    revalidateIfTouched('operationalSuffix', filtered);
  };

  const handleServiceTypeChange = (value: string) => {
    updateDraft('iataServiceTypeCode', value);
    revalidateIfTouched('iataServiceTypeCode', value);
  };

  // Handle Flight Number Digits change (e.g., "1234")
  const handleFlightNumberChange = (value: string) => {
    handleFlightNumberDigitsChange(value);
    const digits = value.replace(/[^0-9]/g, '').slice(0, 4);
    const fullFlightNo = flightNumberPrefix && digits
      ? `${flightNumberPrefix} ${digits}`
      : flightNumberPrefix || digits;
    revalidateIfTouched('flightNo', fullFlightNo);
  };

  return (
    <FormSection title="Flight Information" icon={<FlightTakeoffIcon sx={{ fontSize: 20 }} />} collapsible defaultExpanded>
      <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 2fr 0.8fr 1.2fr' }}>
        {/* Flight Owner */}
        <FormInput
          label="Flight Owner"
          value={draft.flightOwner}
          onChange={handleFlightOwnerChange}
          onBlur={handleFlightOwnerBlur}
          required
          error={getFieldError('flightOwner')}
          disabled={isVariantMode || adhocLocked}
        />

        {/* Flight Number - Split Input with Variant Badge */}
        <div className="relative">
          <SplitFlightNumberInput
            flightNumberPrefix={flightNumberPrefix}
            flightNumberDigits={flightNumberDigits}
            onPrefixChange={handleFlightNumberPrefixChange}
            onDigitsChange={handleFlightNumberChange}
            onBlur={() => handleBlur('flightNo')}
            error={getFieldError('flightNo')}
            disabled={isVariantMode || adhocLocked}
          />
          {/* Variant Badge — hidden in adhoc mode (variant auto-assigned silently) */}
          {!isAdhocMode && (
            <div className="absolute right-2 top-9 flex items-center gap-1">
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                v{editMode ? draft.itineraryVarId : duplicateCheck.nextVariantId}
              </span>
            </div>
          )}
        </div>

        {/* Suffix */}
        <FormInput
          label="Suffix"
          value={draft.operationalSuffix || ''}
          onChange={handleSuffixChange}
          onBlur={() => handleBlur('operationalSuffix')}
          mono
          error={getFieldError('operationalSuffix')}
        />

        {/* Service Type */}
        <ServiceTypeSelect
          value={draft.iataServiceTypeCode}
          onChange={handleServiceTypeChange}
          onBlur={() => handleBlur('iataServiceTypeCode')}
          required
          error={getFieldError('iataServiceTypeCode')}
          disabled={isVariantMode || adhocLocked}
        />
      </div>

      {/* Duplicate Flight Info — hidden in adhoc mode */}
      {duplicateCheck.isDuplicate && !editMode && !isAdhocMode && !isDismissed && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <WarningAmberIcon className="text-blue-700 flex-shrink-0" sx={{ fontSize: 20 }} />
          <p className="font-semibold text-blue-900 text-sm flex-1">
            You are creating Itinerary {duplicateCheck.nextVariantId}
          </p>
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
            title="Dismiss"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      )}
    </FormSection>
  );
}
